import {html, repeat, sync, ViewTemplate} from '@genesislcap/web-core';
import {formatDate, formatNumeric, formatTime} from "../../../../mbm/utils/bond-bid-utils";
import { getBidAuditColumns } from '../../../../mbm/utils/column-config';
import { BondStatusBadgeCellRenderer } from '../../../../mbm/components/bond-status-badge-cell';
import { BidStatusBadgeCellRenderer } from '../../../../mbm/components/bid-status-badge-cell';
import type { TrancheDetailFlyout } from './tranche-detail-flyout';
import {ColDef} from '@ag-grid-community/core';
import {OptionConfig} from '../../../../pbi/components/deals/deal-series/deal-series.util';

const bidPriceTypeOptions: OptionConfig[] = [
  { displayValue: 'Price', value: 'Price' },
  { displayValue: 'Spread', value: 'Spread' },
  { displayValue: 'Yield', value: 'Yield' },
];

function formatBondStatusBadge(status: string, dueTime?: string, firmTime?: string): ViewTemplate<TrancheDetailFlyout> {
  const badgeData = BondStatusBadgeCellRenderer.formatStatusBadge(status, dueTime, firmTime);
  const map = badgeData.map;
  
  return html<TrancheDetailFlyout>`
    <div style="
      background: ${map.bg};
      color: ${map.color};
      display: flex;
      align-items: center;
      border-radius: 20px;
      padding: 0 15px 0 10px;
      height: 28px;
      gap: 2px;
      justify-content: flex-start;
    ">
      <span class="icon">${badgeData.getIcon(map.icon, map.color)}</span>
      <span style="padding-top:3px;">${badgeData.getFormattedText()}</span>
    </div>
  `;
}

function formatBidStatusBadge(status: string, dueTime?: string, firmTime?: string): ViewTemplate<TrancheDetailFlyout> {
  const badgeData = BidStatusBadgeCellRenderer.formatStatusBadge(status, dueTime, firmTime);
  const map = badgeData.map;
  
  return html<TrancheDetailFlyout>`
    <div style="
      background: ${map.bg};
      color: ${map.color};
      display: flex;
      align-items: center;
      border-radius: 20px;
      padding: 0 15px 0 10px;
      height: 28px;
      gap: 2px;
      justify-content: flex-start;
    ">
      <span class="icon">${badgeData.getIcon(map.icon, map.color)}</span>
      <span style="padding-top:3px;">${badgeData.getFormattedText()}</span>
    </div>
  `;
}

export const TrancheDetailFlyoutTemplate = html<TrancheDetailFlyout>`
  <div
    class="resize-handle"
    style=${(x) => x.activeTab === 'overview' ? 'display:none' : ''}
    @mousedown=${(x, c) => x.startResize(c.event as MouseEvent)}
  ></div>
  <div class="content">
    <rapid-tabs activeid=${(x) => x.activeTab}>
      <rapid-tab id="overview" @click=${(x) => x.setActiveTab('overview')}>Overview</rapid-tab>
      <rapid-tab id="history" @click=${(x) => x.setActiveTab('history')}>Audit</rapid-tab>
      <rapid-tab-panel id="overview">
        <div class="flyout-section">
          <div class="flyout-details">

            <div style="display:flex;flex-direction:row;gap:10px;padding-bottom:10px;">
              <div class="detail-col">
                <span class="detail-label">Received</span>
                <span class="detail-value">${(x) => formatTime(x.receivedTime)}</span>
              </div>  
              <div class="detail-col">
                <span class="detail-label">Due</span>
                <span class="detail-value">${(x) => formatTime(x.dueTime)}</span>
              </div>
              <div class="detail-col">
                <span class="detail-label">Firm</span>
                <span class="detail-value">${(x) => formatTime(x.firmTime)}</span>
              </div>
              <div class="detail-col" style="min-width:150px;">
                <span class="detail-label">RFQ Status</span>
                <span class="detail-value">${(x) => formatBondStatusBadge(x.status, x.dueTime, x.firmTime)}</span>
              </div>
            </div>

            <div class="detail-row">
              <span class="detail-label">Issuer</span>
              <span class="detail-value">${(x) => x.issuer}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">State</span>
              <span class="detail-value">${(x) => x.state}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">CUSIP</span>
              <span class="detail-value">${(x) => x.cusip}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Coupon</span>
              <span class="detail-value">${(x) => x.coupon}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Maturity</span>
              <span class="detail-value">${(x) => formatDate(x.maturityDate)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Pre-Refund Date</span>
              <span class="detail-value">${(x) => formatDate(x.preRefundDate)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Call Date</span>
              <span class="detail-value">${(x) => formatDate(x.callDate)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Call Price</span>
              <span class="detail-value">${(x) => x.callPrice}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Workout Date</span>
              <span class="detail-value">${(x) => formatDate(x.workoutDate)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Sector</span>
              <span class="detail-value">${(x) => x.sector}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Ratings (S&P/Fitch/Moodys)</span>
              <span class="detail-value">${(x) => x.issuerRatings}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Fed Taxable</span>
              <span class="detail-value">${(x) => x.taxStatus}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">AMT</span>
              <span class="detail-value">${(x) => x.amt}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Settlement Date</span>
              <span class="detail-value">${(x) => formatDate(x.settlementDate)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Insurer</span>
              <span class="detail-value">${(x) => x.insurer}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Source</span>
              <span class="detail-value">${(x) => x.source}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Quantity</span>
              <span class="detail-value">${(x) => formatNumeric(x.quoteRequestSize)}</span>
            </div>

          </div>
        </div>
      </rapid-tab-panel>
      <rapid-tab-panel id="history">
        <mbm-grid-pro
          enable-row-flashing
          enable-cell-flashing
          style="height: 300px;"
          only-template-col-defs
        >
        ${repeat(
          () => getBidAuditColumns(),
          html<ColDef>`
            <grid-pro-column :definition="${(x) => x}"></grid-pro-column>
          `,
        )}
          
          ${(x) => x.isDatasourceConnected ? html<TrancheDetailFlyout>`
            <grid-pro-genesis-datasource
              resource-name="CPTY_BOND_BID_AUDIT"
              criteria=${(x) => `BOND_ID == '${x.rowData?.BOND_ID || ''}'`}
              max-rows="250"
              max-view="250"
            ></grid-pro-genesis-datasource>
          ` : ''}
        </mbm-grid-pro>
      </rapid-tab-panel>
    </rapid-tabs>

    <rapid-divider></rapid-divider>
      
    <rapid-tabs style="height: 25%;margin-top: 10px;">
      <rapid-tab id="bid-detail" @click=${(x) => x.setActiveTab('overview')}>Bid Details</rapid-tab>
      <rapid-tab-panel id="bid-detail">
       
        <div class="bid-detail-row">
          <div class="detail-col">
            <span class="detail-label">Price</span>
            <span class="detail-value">${(x) => formatNumeric(x.calculatedBidPrice, 3, true)}</span>
          </div>  
          <div class="detail-col">
            <span class="detail-label">Yield</span>
            <span class="detail-value">${(x) => formatNumeric(x.calculatedYield, 3, true)}</span>
          </div>
          <div class="detail-col" style="min-width:150px;">
            <span class="detail-label">Bid Status</span>
            <span class="detail-value">${(x) => x.rowData?.BID_STATUS ? formatBidStatusBadge(x.rowData.BID_STATUS, x.dueTime, x.firmTime) : ''}</span>
          </div>
        </div>

        <rapid-divider></rapid-divider>

        <div class="flyout-form-row">
          <rapid-select class="small-text"
            placeholder="Response Type"
            :value="${sync((x) => x.priceType)}"
            :disabled="${(x) => x.isResponseTypeDisabled()}"
          >
            ${repeat(
              (x) => bidPriceTypeOptions,
              html<OptionConfig>`
                <rapid-option
                  value=${(x) => x.value}
                  :selected=${sync((x, c) => x.value == c.parent.priceType)}
                >
                  ${(x) => x.displayValue}
                </rapid-option>
              `,
            )}
          </rapid-select>
          <rapid-text-field 
            class="small-text"
            placeholder="Price"
            type="number"
            :value="${sync((x) => x.price)}"
            :disabled="${(x) => x.status !== 'DUE_IN'}"
            @blur="${(x) => x.handlePriceBlur()}"
          ></rapid-text-field>
          <rapid-button class="small-text"
            appearance="primary"
            @click=${(x) => x.handleSubmit()}
            :disabled="${(x) => x.isSubmitDisabled()}"
          >
            Submit
          </rapid-button>
        </div>
        
      </rapid-tab-panel>
    </rapid-tabs>
    
  </div>
`;


